<template>
    <div class="pum-properties-render">
        <div v-if="isLoading" class="pum-properties__loading">
            <PtIcon icon="SolidLoadingBars" />
        </div>
        <div v-else-if="isNodata" class="pum-properties__nodata">
            <svg width="32" height="32">
                <use xlink:href="#SolidNoDataTip" />
            </svg>
            <p class="pum-properties__nodata__text">
                {{ t('pumPropertyNodata') }}
            </p>
        </div>
        <div v-else class="pum-properties__table">
            <div class="pum-properties__table__header pum-properties__table__item">
                <span>{{ t('snapBusiness.pum.properties') }}</span>
            </div>
            <div
                v-for="item in pumData"
                :key="`${item.raw_unit}-${item.index}`"
                class="pum-properties__table__main pum-properties__table__item pa-group"
                :class="{
                    'pum-properties__table__main--active': activeIndex === item.index
                }"
                @click="handleItemClick(item.index)"
            >
                <div class="pum-properties__table__main__property">
                    <span>{{ item.display_name }}</span>
                </div>
                <div class="pum-properties__table__main__value">
                    <span class="pum-properties__table__main__value-text">
                        <span>{{ item.raw_measurement }}</span>
                        <span v-html="item.raw_unit" />
                    </span>
                    <svg width="24" height="24" class="pum-properties__table__main__value-icon group-hover:pa-visible">
                        <use xlink:href="#SolidMapPinOn" />
                    </svg>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import '@patsnap-ui/icon/assets/solid/MapPinOn.svg';
import '@patsnap-ui/icon/assets/solid/NoDataTip.svg';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import { ref, computed, watch } from 'vue';
import { Icon as PtIcon } from 'patsnap-biz';
import { useI18n } from '@/composables/use-i18n';
import { useDomNodesTip } from '@/components/dom-nodes-tip/use-dom-nodes-tip';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { GetPatentPum } from '@/infrastructure/api-functions/core-search-api/get-patent-pum-api';
import { useTranslationStates } from '@/composables/use-translation-states';

interface PropertyItem {
    index: number;
    raw_unit: string;
    display_name: string;
    raw_measurement: string;
}

const props = defineProps<{
    patentId: string;
}>();

const { t } = useI18n();
const { initNodesTip } = useDomNodesTip();
const { currentTranslateLanguages } = useTranslationStates();

const getPatentPumApi = injectService<GetPatentPum>(GetPatentPum);

const activeIndex = ref<number | null>(null);

const pumData = ref<PropertyItem[]>([]);

const isLoading = ref(true);

const isNodata = computed(() => !pumData.value.length);

const handleItemClick = (index: number) => {
    activeIndex.value = index;
    const selector = `${props.patentId}-pum-${index}`;
    initNodesTip({
        selector,
        notFoundMessage: t('pumParaNotFound'),
    });
};

const getPumData = async () => {
    isLoading.value = true;
    const lang = currentTranslateLanguages.value.left;
    const res = await getPatentPumApi.getPatentPum({
        lang,
        patentId: props.patentId,
    });
    pumData.value = (res.MATERIAL_PUM_TRAN?.length > 0 ? res.MATERIAL_PUM_TRAN : res.MATERIAL_PUM) || [];
    isLoading.value = false;
};

watch(currentTranslateLanguages, () => {
    getPumData();
}, { immediate: true });

</script>

<style lang="scss">
.pum-properties-render {
    @apply pa-h-full pa-w-full pa-p-4 pa-text-gray-1100;

    .pum-properties__table {
        @apply pa-rounded pa-overflow-hidden pa-pb-6;
        &__header {
            @apply pa-bg-blue-40 pa-flex pa-items-center pa-font-semibold;
        }

        &__item {
            @apply pa-px-2 pa-min-h-[48px];
        }

        &__main {
            @apply pa-flex pa-leading-5 odd:pa-bg-gray-20 hover:pa-bg-blue-75 pa-cursor-pointer;
            &__property {
                @apply pa-max-w-[160px] pa-min-w-[100px] pa-flex-shrink-0 pa-line-clamp-1 pa-px-2 pa-py-1 pa-flex pa-items-center;
            }
            &__value {
                @apply pa-flex pa-flex-1 pa-items-center pa-justify-end;

                &-text {
                    @apply pa-text-right;
                }

                &-icon {
                    @apply pa-shrink-0 pa-invisible pa-fill-gray-55 hover:pa-fill-gray-90;
                }
            }

            &--active {
                @apply pa-border pa-border-blue-500 pa-rounded pa-bg-blue-75 #{!important};
                .pum-properties__table__main__value-icon {
                    @apply pa-fill-blue-500 pa-visible #{!important};
                }
            }
        }
    }

    .pum-properties__nodata {
        @apply pa-mt-6 pa-flex pa-flex-col pa-items-center pa-justify-center;

        &__text {
            @apply pa-mt-3 pa-leading-5 pa-text-gray-90 pa-text-center;
        }
    }
    
    .pum-properties__loading {
        @apply pa-flex pa-items-center pa-justify-center pa-mt-3 pa-h-full;
        svg {
            @apply pa-text-[32px];
        }
    }
}
</style>
